import { CategoryDto, DataMapDto } from "../../server/dto/datamap/DataMapDto";

export const INFERENCES_BASED_ON_SENSITIVE_INFORMATION_PIC_KEY =
  "inferences-based-on-sensitive-information";

export type SensitivePICFilter = {
  /**
   * If provided, will only include PICs that are in this list.
   * Note that if provided, this list will always override any other option.
   */
  filterPicIds?: string[];

  /**
   * Whether to include the "Inferences based on sensitive information" PIC.
   * Note that this does *not* override the `filterPicIds` option, so this option is a no-op if `filterPicIds` is also provided and doesn't include the sensitive inferences PIC ID.
   */
  includeInferencesBasedOnSensitiveInformationPIC?: boolean;

  /**
   * Whether a PIC must be sensitive in CA to be included.
   * Note that this does *not* apply to the "Inferences based on sensitive information" PIC.
   */
  mustBeSensitiveInCA?: boolean;

  /**
   * If true, will exlucde any sensitive PIC if it is only sensitive in CA.
   * Note that this does *not* apply to the "Inferences based on sensitive information" PIC.
   */
  excludeIfOnlySensitiveInCA?: boolean;
};

/**
 * Returns a list of sensitive PICs for the given filter conditions.
 */
export const getSensitivePICsWithFilters = (
  dataMap: DataMapDto,
  filter?: SensitivePICFilter,
): CategoryDto[] => {
  if (!dataMap) {
    return [];
  }

  let pics = Object.values(dataMap.categories);

  return pics.filter((pic) => {
    // First check `filterPicIds`, if provided
    if (filter?.filterPicIds && !filter.filterPicIds.includes(pic.id)) {
      return false;
    }

    // Handle the "Inferences based on sensitive information" PIC
    if (pic.key === INFERENCES_BASED_ON_SENSITIVE_INFORMATION_PIC_KEY) {
      return filter?.includeInferencesBasedOnSensitiveInformationPIC === true;
    }

    let isSensitive = pic.sensitiveRegions?.length > 0;

    // The two remaining filters are `mustBeSensitiveInCA` and `excludeIfOnlySensitiveInCA`
    let isSensitiveInCA =
      isSensitive && pic.sensitiveRegions.map((r) => r.toLowerCase()).includes("ca");
    let isOnlySensitiveInCA = isSensitiveInCA && pic.sensitiveRegions?.length == 1;

    // If both filters are true, then only include the PIC if it is sensitive in CA *and* also sensitive in other regions
    if (filter?.mustBeSensitiveInCA === true && filter?.excludeIfOnlySensitiveInCA === true) {
      return isSensitiveInCA && !isOnlySensitiveInCA;
    } else if (filter?.mustBeSensitiveInCA === true) {
      return isSensitiveInCA;
    } else if (filter?.excludeIfOnlySensitiveInCA === true) {
      return isSensitive && !isOnlySensitiveInCA;
    }

    return isSensitive;
  });
};
